import { useMemo, useRef, useState } from 'react';

import { debounce } from 'lodash';

import { useProvidersSearchData } from '../InquiryDetail/useProvidersSearchData';
import { getMenuOptions } from '../InquiryDetail/viewUtil';
import { UnassignedUniversalSelectOption } from '~/lib/constants/inquiry.constants';
import {
  CareTeamProviderType,
  CareTeamProviderTypeNames,
} from '~/lib/constants/patients.constants';
import { doesCurrentUserHaveRole } from '~/lib/util/user.util';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';

export type UseProvidersFilterDataProps = {
  pageSize?: number;
  providerType: CareTeamProviderType;
};

type ProvidersFilterData =
  InquiryPSRFilterProps['patientServiceRepresentatives'];
type ProviderOptions = ProvidersFilterData['providerOptions'];

const unassignedProviderOption: ProviderOption = {
  firstName: UnassignedUniversalSelectOption.label,
  lastName: '',
  // the userId is used as the value in the query param, e.g. /providers?psr-external-id::{userId}
  // this value should be "none" when searching for unassigned PSRs, e.g. /providers?psr-external-id::none
  userId: UnassignedUniversalSelectOption.value,
};

export const useProvidersFilterData = ({
  pageSize = 15,
  providerType,
}: UseProvidersFilterDataProps): ProvidersFilterData => {
  const providers = useProvidersSearchData({ pageSize });

  const { currentUser } = useGlobalContext();

  const [pageNumber, setPageNumber] = useState<number>(1);

  const debouncedFetchOptions = useRef(
    debounce((searchTerm: string, pageNumber: number) => {
      providers.handleSearch(
        searchTerm,
        pageNumber,
        CareTeamProviderTypeNames[providerType],
      );
    }, 500),
  ).current;

  const loadOptions = (inputVal: string): void => {
    const newPageNumber = 1;
    debouncedFetchOptions(inputVal, newPageNumber);
    setPageNumber(newPageNumber);
    if (inputVal !== providers.searchInput) {
      providers.setSearchInput(inputVal);
    }
  };

  const loadMoreOptions = (): void => {
    if (providers.totalCount === providers.dataList.length) {
      return;
    }
    const newPageNumber = pageNumber + 1;
    debouncedFetchOptions(providers.searchInput, newPageNumber);
    setPageNumber(newPageNumber);
  };

  const providerOptions = useMemo<ProviderOptions>(() => {
    // If current user has role providerRole then their name should show up after unassigned.
    const alwaysPresent = doesCurrentUserHaveRole(currentUser, providerType)
      ? [
          unassignedProviderOption,
          {
            firstName: currentUser.firstName,
            lastName: currentUser.lastName,
            userId: currentUser.userId,
          },
        ]
      : [unassignedProviderOption];

    const dataList = [...alwaysPresent, ...providers.dataList];
    return getMenuOptions('inquiry-filter', {
      ...providers,
      dataList,
    }) as ProviderOptions;
  }, [providers, currentUser, providerType]);

  return {
    inputValue: providers.searchInput,
    isLoading: providers.isLoading,
    onInputChange: loadOptions,
    onMenuScrollToBottom: loadMoreOptions,
    providerOptions,
  };
};
